import { getBuilderZoneRanges, getCategoryFromPrimaryMetric } from './intensityMetricHelpers';
import type { PrimaryIntensityMetric } from './workoutBuilderSettings';
import type { ApiWorkoutStructure } from './workoutStructure';

export class WorkoutZoneUnavailableError extends Error {
  constructor(public readonly zone: string) {
    super(`Zone ${zone} has no usable range for this athlete.`);
  }
}

const normalizedLabel = (value: string) => value.trim().toLowerCase()
  .replace(/[۰-۹]/g, digit => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
  .replace(/[٠-٩]/g, digit => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)));

/** Apply only to newly generated steps explicitly named for a zone. Never run on saved/manual workouts. */
export function applyAthleteWorkoutZones(structure: ApiWorkoutStructure, settings: Record<string, unknown>): ApiWorkoutStructure {
  const metric = structure.primaryIntensityMetric as PrimaryIntensityMetric;
  const category = getCategoryFromPrimaryMetric(metric);
  if (!category) return structure;
  const key = category === 'power' ? 'power_zones' : category === 'hr' ? 'heart_rate_zones' : 'speed_zones';
  const block = settings[key] as { zones?: { label?: string }[]; range_percent?: { label?: string }[]; threshold?: number } | undefined;
  const rows = block?.zones ?? block?.range_percent ?? [];
  const thresholds = {
    ftp: Number(settings.ftp ?? (settings.power_zones as { threshold?: number })?.threshold),
    ltHr: Number(settings.lt_hr ?? (settings.heart_rate_zones as { threshold?: number })?.threshold),
    maxHr: Number(settings.max_hr),
    thresholdSpeedMps: Number((settings.speed_zones as { threshold?: number })?.threshold),
  };
  let changed = false;
  const result = { ...structure, structure: structure.structure.map(blockItem => ({ ...blockItem, steps: blockItem.steps?.map(step => {
    const name = normalizedLabel(step.name ?? '');
    const numbered = /^(?:z|zone|ناحیه|زون)\s*([1-9][0-9]*)$/i.exec(name);
    const index = numbered ? Number(numbered[1]) - 1 : rows.findIndex(row => !!row.label && normalizedLabel(row.label) === name);
    if (numbered && !rows[index]) throw new WorkoutZoneUnavailableError(step.name ?? name);
    if (index < 0 || !rows[index]) return step;
    // Convert one original row so invalid/open preceding zones cannot shift zone numbering.
    const zone = getBuilderZoneRanges({ [key]: { [block?.zones ? 'zones' : 'range_percent']: [rows[index]] } }, metric, thresholds)[0];
    if (!zone) throw new WorkoutZoneUnavailableError(step.name ?? name);
    const target = step.targets?.[0];
    if (Number(target?.minValue) === zone.minimum && Number(target?.maxValue) === zone.maximum) return step;
    changed = true;
    return { ...step, targets: [{ ...target, minValue: zone.minimum, maxValue: zone.maximum }, ...(step.targets?.slice(1) ?? [])] };
  }) })) };
  return changed ? result : structure;
}
